/**
 * services/quant/decisionQuality.ts
 *
 * Decision Quality Score (DQS) — MER Algo Footprints v5.3
 *
 * Scores the quality of a trading DECISION independently of its eventual P/L outcome.
 * A losing trade can still have a high DQS (good process, bad luck).
 * A winning trade can still have a weak DQS (poor process, lucky outcome).
 *
 * Non-executing, observational only. No advisory language.
 */

import { AuditRecord } from '../../../../types';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface DecisionQualityBreakdown {
  contextAlignment:     number;   // 0-100: trend/phase/regime alignment
  confidenceDiscipline: number;   // 0-100: was confidence appropriate for context?
  structureQuality:     number;   // 0-100: entry/stop/target presence and coherence
  riskQuality:          number;   // 0-100: implied risk-reward coherence
  executionReadiness:   number;   // 0-100: readiness tier quality
}

export interface DecisionQualityResult {
  score:     number;   // 0-100 composite
  tier:      'elite' | 'strong' | 'acceptable' | 'weak' | 'poor';
  breakdown: DecisionQualityBreakdown;
  summary:   string;
  warnings:  string[];
}

export interface DecisionQualitySummary {
  avgScore:    number | null;
  byTier:      Record<DecisionQualityResult['tier'], number>;
  bySetupType: Record<string, { avgScore: number; count: number }>;
  byRegime:    Record<string, { avgScore: number; count: number }>;
  // Top and bottom 5 decisions by DQS — process-focused, not outcome-based
  topDecisions:    Array<{ id: string; symbol: string; setupType: string; score: number; tier: DecisionQualityResult['tier'] }>;
  bottomDecisions: Array<{ id: string; symbol: string; setupType: string; score: number; tier: DecisionQualityResult['tier'] }>;
}

// ─── Scoring weights ──────────────────────────────────────────────────────────

const WEIGHTS = {
  contextAlignment:     0.30,
  confidenceDiscipline: 0.20,
  structureQuality:     0.20,
  riskQuality:          0.15,
  executionReadiness:   0.15,
} as const;

// ─── Component scorers ────────────────────────────────────────────────────────

function scoreContextAlignment(r: AuditRecord): number {
  let score = 50; // baseline

  // Trend alignment
  if (r.trendAlignment === 'aligned_with_trend') score += 25;
  else if (r.trendAlignment === 'neutral_trend_context') score += 5;
  else if (r.trendAlignment === 'counter_trend') score -= 15;

  // Phase
  if (r.phase === 'EXPANSION') score += 15;
  else if (r.phase === 'POST_EXPANSION') score += 8;
  else if (r.phase === 'RANGE') score += 0;

  // Regime fit (continuation setups in trending, reversal in range)
  const setupLower = r.setupType.toLowerCase();
  const isContinuation = setupLower.includes('continuation');
  const isReversal = setupLower.includes('reversal') || setupLower.includes('fade') || setupLower.includes('reversion');
  if (isContinuation && r.phase === 'EXPANSION') score += 10;
  if (isReversal && r.phase === 'RANGE') score += 8;
  if (isContinuation && r.phase === 'RANGE') score -= 8;
  if (isReversal && r.phase === 'EXPANSION') score -= 10;

  return Math.max(0, Math.min(100, score));
}

function scoreConfidenceDiscipline(r: AuditRecord): number {
  let score = 60;

  // Penalise high confidence outside aligned context — stronger and broader than before
  if (r.confidence >= 80 && r.trendAlignment !== 'aligned_with_trend') {
    score -= 10;  // applies to both counter_trend AND neutral (not just weak context)
  }
  // Penalise very high confidence in genuinely weak context (additional hit)
  const isWeakContext = r.trendAlignment === 'counter_trend' && r.phase === 'RANGE';
  if (isWeakContext && r.confidence >= 80) score -= 20;   // cumulative: −30 total from above
  else if (isWeakContext && r.confidence >= 65) score -= 15;

  // Reward moderate confidence in mixed context (appropriate humility)
  const isMixedContext = r.trendAlignment === 'neutral_trend_context';
  if (isMixedContext && r.confidence >= 40 && r.confidence <= 70) score += 15;

  // Reward appropriate confidence in strong context
  const isStrongContext = r.trendAlignment === 'aligned_with_trend' && r.phase === 'EXPANSION';
  if (isStrongContext && r.confidence >= 65) score += 20;

  // Penalise under-confidence in strong aligned expansion context
  if (r.confidence < 40 && r.trendAlignment === 'aligned_with_trend' && r.phase === 'EXPANSION') {
    score -= 5;
  }

  return Math.max(0, Math.min(100, score));
}

function scoreStructureQuality(r: AuditRecord): number {
  let score = 0;

  // Entry level
  if (r.entryPrice !== null) score += 25;
  // Stop level
  if (r.stopPrice !== null) score += 25;
  // At least one target
  if (r.target1Price !== null) score += 25;
  // Both targets
  if (r.target2Price !== null) score += 10;

  // Coherence: entry, stop, and target all present and in sensible order
  if (r.entryPrice !== null && r.stopPrice !== null && r.target1Price !== null) {
    const isLong  = r.direction === 'long';
    const entryAboveStop  = r.entryPrice > r.stopPrice;
    const targetAboveEntry = r.target1Price > r.entryPrice;
    if (isLong && entryAboveStop && targetAboveEntry) score += 15;
    if (!isLong && !entryAboveStop && !targetAboveEntry) score += 15;
  }

  return Math.max(0, Math.min(100, score));
}

function scoreRiskQuality(r: AuditRecord): number {
  if (r.entryPrice === null || r.stopPrice === null) return 30; // incomplete — below average
  if (r.target1Price === null) return 40;

  const riskPerR = Math.abs(r.entryPrice - r.stopPrice);
  if (riskPerR <= 0) return 20;  // zero or negative risk — incoherent

  const rewardR = Math.abs(r.target1Price - r.entryPrice);
  const rrRatio = rewardR / riskPerR;

  // Score based on risk-reward ratio
  if (rrRatio >= 3.0)   return 100;
  if (rrRatio >= 2.0)   return 85;
  if (rrRatio >= 1.5)   return 70;
  if (rrRatio >= 1.0)   return 55;
  if (rrRatio >= 0.75)  return 40;
  return 20;  // poor R:R
}

function scoreExecutionReadiness(r: AuditRecord): number {
  switch (r.readiness) {
    case 'urgent':      return 100;
    case 'ready':       return 80;
    case 'good':        return 55;
    case 'interesting': return 30;
    default:            return 40;
  }
}

function scoreTier(score: number): DecisionQualityResult['tier'] {
  if (score >= 82) return 'elite';
  if (score >= 68) return 'strong';
  if (score >= 52) return 'acceptable';
  if (score >= 36) return 'weak';
  return 'poor';
}

function buildWarnings(r: AuditRecord, bd: DecisionQualityBreakdown): string[] {
  const w: string[] = [];
  if (r.entryPrice === null)  w.push('No entry price recorded — structure quality limited.');
  if (r.stopPrice  === null)  w.push('No stop price recorded — risk quality cannot be assessed.');
  if (r.trendAlignment === 'counter_trend')
    w.push('Counter-trend setup — context alignment penalty applied.');
  if (r.confidence >= 80 && r.trendAlignment === 'counter_trend')
    w.push('High confidence in counter-trend context — confidence discipline penalty applied.');
  if (bd.riskQuality < 40)
    w.push('Risk-reward structure is below threshold — consider tighter targets.');
  return w;
}

// ─── Per-record DQS ───────────────────────────────────────────────────────────

export function computeDecisionQuality(record: AuditRecord): DecisionQualityResult {
  const breakdown: DecisionQualityBreakdown = {
    contextAlignment:     Math.round(scoreContextAlignment(record)),
    confidenceDiscipline: Math.round(scoreConfidenceDiscipline(record)),
    structureQuality:     Math.round(scoreStructureQuality(record)),
    riskQuality:          Math.round(scoreRiskQuality(record)),
    executionReadiness:   Math.round(scoreExecutionReadiness(record)),
  };

  const score = Math.round(
    breakdown.contextAlignment     * WEIGHTS.contextAlignment     +
    breakdown.confidenceDiscipline * WEIGHTS.confidenceDiscipline +
    breakdown.structureQuality     * WEIGHTS.structureQuality     +
    breakdown.riskQuality          * WEIGHTS.riskQuality          +
    breakdown.executionReadiness   * WEIGHTS.executionReadiness,
  );

  const tier = scoreTier(score);
  const warnings = buildWarnings(record, breakdown);

  const summary = `DQS ${score}/100 (${tier}). ` +
    `Context: ${breakdown.contextAlignment} · ` +
    `Discipline: ${breakdown.confidenceDiscipline} · ` +
    `Structure: ${breakdown.structureQuality} · ` +
    `Risk-Reward: ${breakdown.riskQuality} · ` +
    `Readiness: ${breakdown.executionReadiness}.`;

  return { score, tier, breakdown, summary, warnings };
}

// ─── Aggregate DQS summary ────────────────────────────────────────────────────

export function computeDecisionQualitySummary(
  records: AuditRecord[],
): DecisionQualitySummary {
  if (!records.length) {
    return {
      avgScore: null,
      byTier: { elite: 0, strong: 0, acceptable: 0, weak: 0, poor: 0 },
      bySetupType: {},
      byRegime: {},
      topDecisions:    [],
      bottomDecisions: [],
    };
  }

  const results = records.map(r => ({ r, dqs: computeDecisionQuality(r) }));

  const avgScore = Math.round(
    results.reduce((s, { dqs }) => s + dqs.score, 0) / results.length,
  );

  const byTier: DecisionQualitySummary['byTier'] = {
    elite: 0, strong: 0, acceptable: 0, weak: 0, poor: 0,
  };
  for (const { dqs } of results) byTier[dqs.tier]++;

  // bySetupType: avg DQS per setup family
  const setupGroups: Record<string, number[]> = {};
  for (const { r, dqs } of results) {
    if (!setupGroups[r.setupType]) setupGroups[r.setupType] = [];
    setupGroups[r.setupType].push(dqs.score);
  }
  const bySetupType: DecisionQualitySummary['bySetupType'] = {};
  for (const [st, scores] of Object.entries(setupGroups)) {
    bySetupType[st] = {
      avgScore: Math.round(scores.reduce((s, v) => s + v, 0) / scores.length),
      count:    scores.length,
    };
  }

  // byRegime: avg DQS per regime
  const regimeGroups: Record<string, number[]> = {};
  for (const { r, dqs } of results) {
    if (!regimeGroups[r.regime]) regimeGroups[r.regime] = [];
    regimeGroups[r.regime].push(dqs.score);
  }
  const byRegime: DecisionQualitySummary['byRegime'] = {};
  for (const [reg, scores] of Object.entries(regimeGroups)) {
    byRegime[reg] = {
      avgScore: Math.round(scores.reduce((s, v) => s + v, 0) / scores.length),
      count:    scores.length,
    };
  }

  // Top/bottom 5 decisions by DQS score — observational, process-focused only
  const sortedDesc = [...results].sort((a, b) => b.dqs.score - a.dqs.score);
  const toEntry = ({ r, dqs }: { r: AuditRecord; dqs: DecisionQualityResult }) => ({
    id:        r.id,
    symbol:    r.symbol,
    setupType: r.setupType,
    score:     dqs.score,
    tier:      dqs.tier,
  });
  const topDecisions    = sortedDesc.slice(0, 5).map(toEntry);
  const bottomDecisions = [...results].sort((a, b) => a.dqs.score - b.dqs.score).slice(0, 5).map(toEntry);

  return { avgScore, byTier, bySetupType, byRegime, topDecisions, bottomDecisions };
}
